import { useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardCopy,
  KeyRound,
  Link2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { CredentialHealth } from "@/components/credentials/CredentialShell";
import { validationFeedback } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { cn } from "@/lib/utils";
import { gateReasonFromError, type GateReason } from "@/lib/plan-gates";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";

interface MercadoLivreCredentialSectionProps {
  readonly credential: AffiliateCredentialPublic | undefined;
  readonly loadingCredentials: boolean;
}

export function MercadoLivreCredentialSection({
  credential,
  loadingCredentials,
}: MercadoLivreCredentialSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [tag, setTag] = useState("");
  const [mattTool, setMattTool] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [extensionToken, setExtensionToken] = useState("");
  const [tokenStatus, setTokenStatus] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [tokenGate, setTokenGate] = useState<GateReason | null>(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copyingToken, setCopyingToken] = useState(false);

  const handleSaveCredential = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const normalizedTag = tag.trim();
    const normalizedMattTool = mattTool.trim() || null;
    setStatus(null);
    setError(null);

    if (!normalizedTag) {
      setError("Informe sua tag de afiliado do Mercado Livre.");
      return;
    }

    setSaving(true);
    const { error: rpcError } = await supabase.rpc("upsert_mercadolivre_affiliate_credential", {
      p_tag: normalizedTag,
      p_matt_tool: normalizedMattTool,
    });
    setSaving(false);

    if (rpcError) {
      setError(mercadoLivreRpcErrorLabel(rpcError.message));
      return;
    }

    setTag("");
    setMattTool("");
    const feedback = await validationFeedback("mercadolivre");
    setStatus(feedback.status);
    setError(feedback.error);
    await queryClient.invalidateQueries({ queryKey: ["affiliate-credentials"] });
  };

  const handleCreateExtensionToken = async (): Promise<void> => {
    setTokenStatus(null);
    setTokenError(null);
    setTokenGate(null);
    setExtensionToken("");
    setGeneratingToken(true);

    const { data, error: rpcError } = await supabase.rpc("create_mercadolivre_extension_token");
    setGeneratingToken(false);

    const gate = gateReasonFromError(rpcError);
    if (gate) {
      setTokenGate(gate);
      return;
    }
    if (rpcError || typeof data !== "string" || !data) {
      setTokenError("Não foi possível gerar o token de extensão agora.");
      return;
    }

    setExtensionToken(data);
    setTokenStatus("Token de extensão gerado. Use este token para parear a extensão LucraLink.");
  };

  const handleCopyToken = async (): Promise<void> => {
    if (!extensionToken) return;
    setCopyingToken(true);
    await navigator.clipboard.writeText(extensionToken);
    setCopyingToken(false);
    setTokenStatus("Token copiado.");
  };

  return (
    <section
      className="overflow-hidden rounded-xl border border-border bg-card"
      aria-labelledby="section-mercadolivre"
      id="platform-mercadolivre"
    >
      <div className="flex items-center justify-between gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
        <h2
          id="section-mercadolivre"
          className="text-xs font-semibold uppercase tracking-widest text-muted-foreground"
        >
          Mercado Livre
        </h2>
        {loadingCredentials && (
          <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            carregando
          </span>
        )}
      </div>

      <div className="space-y-5 p-5">
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-muted-foreground font-medium">Identidade do afiliado</p>
          {!loadingCredentials && credential && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Link2 size={15} className="text-primary" />
              <span className="font-mono">Tag {credential.tag_masked}</span>
              {credentialConfigValue(credential, "mattTool") ? (
                <span className="font-mono">matt_tool {credentialConfigValue(credential, "mattTool")}</span>
              ) : null}
              <StatusBadge status={credential.status} />
            </div>
          )}

          {!loadingCredentials && !credential && (
            <p className="text-sm text-muted-foreground">Nenhuma configuração salva para Mercado Livre.</p>
          )}
        </div>

        <CredentialHealth credential={credential} />

        <form onSubmit={handleSaveCredential} className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="mercadolivre-tag" variant="field">Tag do sub-id</Label>
            <Input
              id="mercadolivre-tag"
              value={tag}
              onChange={(event) => setTag(event.target.value)}
              placeholder="sualoja"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mercadolivre-matt-tool" variant="field">matt_tool (opcional)</Label>
            <Input
              id="mercadolivre-matt-tool"
              value={mattTool}
              onChange={(event) => setMattTool(event.target.value)}
              placeholder="sua_matt_tool"
              autoComplete="off"
            />
          </div>

          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" variant="gold" disabled={saving}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
              Salvar tag
            </Button>
          </div>
        </form>

        {status && (
          <InlineNotice tone="success" icon={<CheckCircle2 size={15} />}>
            {status}
          </InlineNotice>
        )}

        {error && (
          <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
            {error}
          </InlineNotice>
        )}

        <div className="space-y-2 border-t border-border pt-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground font-medium">
              Token de extensão LucraLink Capture
            </p>
            <p className="text-xs text-muted-foreground">
              Gere o token para parear a extensão do Mercado Livre com este tenant.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button type="button" variant="outline-gold" onClick={handleCreateExtensionToken} disabled={generatingToken}>
              {generatingToken ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
              Gerar token de extensão
            </Button>

            {extensionToken && (
              <div className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-[hsl(var(--surface)/0.7)] px-3 py-2">
                <p className="min-w-0 flex-1 truncate font-mono text-sm text-foreground">{extensionToken}</p>
                <Button
                  type="button"
                  size="icon-sm"
                  variant="outline"
                  onClick={handleCopyToken}
                  aria-label="Copiar token"
                >
                  {copyingToken ? <Loader2 size={14} className="animate-spin" /> : <ClipboardCopy size={14} />}
                </Button>
              </div>
            )}
          </div>

          {tokenStatus && (
            <InlineNotice tone="success" icon={<CheckCircle2 size={15} />}>
              {tokenStatus}
            </InlineNotice>
          )}

          {tokenGate && <UpgradePrompt reason={tokenGate} variant="inline" />}

          {tokenError && (
            <InlineNotice tone="danger" icon={<AlertCircle size={15} />}>
              {tokenError}
            </InlineNotice>
          )}
        </div>
      </div>
    </section>
  );
}

function InlineNotice({
  children,
  icon,
  tone,
}: {
  children: ReactNode;
  icon: ReactNode;
  tone: "success" | "danger";
}): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm",
        tone === "success"
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-destructive/30 bg-destructive/10 text-destructive",
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const active = status === "valid";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
        active
          ? "border-primary/25 bg-primary/10 text-primary"
          : "border-warning/25 bg-warning/10 text-warning",
      )}
    >
      {active ? "ativa" : status}
    </span>
  );
}

function credentialConfigValue(credential: AffiliateCredentialPublic, key: string): string | null {
  const value = credential.config_public?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function mercadoLivreRpcErrorLabel(message: string): string {
  if (message.includes("invalid_mercadolivre_tag")) {
    return "Tag inválida. Use apenas letras, números, hífen ou underscore.";
  }
  if (message.includes("invalid_mercadolivre_matt_tool")) {
    return "matt_tool inválido. Use letras, números, hífen ou underscore.";
  }
  if (message.includes("tenant_not_found")) return "Tenant ativo não encontrado.";
  return "Não foi possível salvar a configuração do Mercado Livre.";
}
